import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { useCart } from "@/contexts/CartContext";
import { formatRupiah } from "@/lib/api";
import { ArrowLeft, Minus, Plus, Trash2, ShoppingBag } from "lucide-react";

export default function Cart() {
  const navigate = useNavigate();
  const { items, updateQty, removeItem, subtotal, totalCount, tableNumber, setTableNumber } = useCart();

  if (totalCount === 0) {
    return (
      <div className="min-h-screen bg-[#F9F9F6] flex items-center justify-center p-6">
        <div className="text-center">
          <ShoppingBag className="w-12 h-12 text-[#5C6A61]/40 mx-auto mb-3" />
          <p className="text-[#5C6A61] mb-4">Keranjang kosong</p>
          <Link to="/" data-testid="btn-back-menu" className="text-[#C25934] font-medium">← Kembali ke Menu</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9F6] pb-32">
      <header className="sticky top-0 z-20 bg-white border-b border-[#EAE7DF] px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate(-1)} data-testid="btn-cart-back" className="p-2"><ArrowLeft className="w-5 h-5 text-[#1E3A2B]" /></button>
        <div className="font-display text-lg font-semibold text-[#1E3A2B]">Keranjang</div>
      </header>

      <div className="max-w-xl mx-auto p-4 space-y-3">
        <div className="bg-white rounded-2xl border border-[#EAE7DF] p-4">
          <label className="block">
            <span className="text-xs uppercase tracking-widest font-bold text-[#5C6A61]">Nomor Meja</span>
            <input data-testid="input-table-number" value={tableNumber} onChange={(e) => setTableNumber(e.target.value)} placeholder="Masukkan nomor meja" className="mt-1 w-full border border-[#D3CEC4] rounded-xl px-3 py-2.5" />
          </label>
        </div>

        <div className="bg-white rounded-2xl border border-[#EAE7DF] divide-y divide-[#EAE7DF]">
          {items.map((it) => {
            const optSum = (it.options || []).reduce((a, o) => a + o.price_delta, 0);
            const line = (it.unit_price + optSum) * it.quantity;
            return (
              <div key={it.cart_id} className="p-4 flex gap-3" data-testid={`cart-item-${it.cart_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-[#1E3A2B]">{it.name}</div>
                  {it.options?.length > 0 && <div className="text-xs text-[#5C6A61] mt-0.5">{it.options.map((o) => o.name).join(", ")}</div>}
                  {it.notes && <div className="text-xs text-[#C25934] italic mt-0.5">{it.notes}</div>}
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex items-center gap-2 border border-[#D3CEC4] rounded-full px-1">
                      <button onClick={() => updateQty(it.cart_id, it.quantity - 1)} data-testid={`btn-cart-minus-${it.cart_id}`} className="w-8 h-8 flex items-center justify-center"><Minus className="w-3.5 h-3.5" /></button>
                      <span className="w-6 text-center text-sm">{it.quantity}</span>
                      <button onClick={() => updateQty(it.cart_id, it.quantity + 1)} data-testid={`btn-cart-plus-${it.cart_id}`} className="w-8 h-8 flex items-center justify-center"><Plus className="w-3.5 h-3.5" /></button>
                    </div>
                    <div className="font-display font-semibold text-[#1E3A2B]">{formatRupiah(line)}</div>
                  </div>
                </div>
                <button onClick={() => removeItem(it.cart_id)} data-testid={`btn-cart-remove-${it.cart_id}`} className="p-2 self-start"><Trash2 className="w-4 h-4 text-[#D64045]" /></button>
              </div>
            );
          })}
        </div>

        <div className="bg-white rounded-2xl border border-[#EAE7DF] p-4">
          <div className="flex items-center justify-between text-sm">
            <span className="text-[#5C6A61]">Subtotal ({totalCount} item)</span>
            <span className="text-[#1E3A2B] font-medium" data-testid="cart-subtotal">{formatRupiah(subtotal)}</span>
          </div>
        </div>
      </div>

      <div className="fixed bottom-0 inset-x-0 bg-white border-t border-[#EAE7DF] p-4">
        <button
          onClick={() => navigate("/checkout")}
          disabled={!tableNumber}
          data-testid="btn-checkout"
          className="w-full max-w-xl mx-auto block bg-[#C25934] text-white hover:bg-[#A94726] py-3.5 rounded-2xl font-medium disabled:opacity-50"
        >
          Lanjut ke Pembayaran · {formatRupiah(subtotal)}
        </button>
      </div>
    </div>
  );
}
